import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Power, Coins, Plus, Sparkles, BookMarked, ScrollText, KeyRound, Trash2, ShoppingCart, BarChart3 } from "lucide-react";
import { RecaptchaSettings } from "../components/RecaptchaSettings";

const ROLE_LABEL = { SUPER_ADMIN: "Matriz", FRANQUICIA: "Franquicia", PROFESOR: "Profesor", ALUMNO: "Alumno" };

export default function Admin() {
  const { user } = useAuth();
  const isMatriz = user.role === "SUPER_ADMIN";
  const isFranq = user.role === "FRANQUICIA";
  const tabs = isMatriz
    ? ["Franquicias", "Usuarios", "Dakota IA", "Créditos", "WooCommerce", "Analítica", "Comentarios", "Conocimiento Dakota", "Listas de Valores", "Generar Artículo IA", "Auditoría", "Seguridad"]
    : isFranq ? ["Usuarios", "Créditos", "Comentarios", "Analítica"] : ["Mis Alumnos"];
  const [tab, setTab] = useState(tabs[0]);

  return (
    <div className="fade-up" data-testid="admin-page">
      <h1 className="text-3xl font-semibold tracking-tight mb-1">Gestión</h1>
      <p className="text-neutral-500 mb-6">{isMatriz ? "Panel matriz: control total de Natur Academy." : isFranq ? "Gestiona los usuarios de tu franquicia." : "Tus alumnos asignados."}</p>
      <div className="flex flex-wrap gap-2 mb-8">
        {tabs.map((t) => (
          <button key={t} data-testid={`admin-tab-${t.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setTab(t)}
                  className={`text-sm px-4 py-2 rounded-full transition-colors ${tab === t ? "bg-neutral-900 text-white" : "bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50"}`}>
            {t}
          </button>
        ))}
      </div>
      {tab === "Franquicias" && <Franchises />}
      {(tab === "Usuarios" || tab === "Mis Alumnos") && <UsersTab />}
      {tab === "Dakota IA" && <KillSwitch />}
      {tab === "Créditos" && <Credits />}
      {tab === "WooCommerce" && <WooTab />}
      {tab === "Analítica" && <AnalyticsTab />}
      {tab === "Comentarios" && <CommentsTab />}
      {tab === "Conocimiento Dakota" && <Knowledge />}
      {tab === "Listas de Valores" && <LovManager />}
      {tab === "Generar Artículo IA" && <ArticleGen />}
      {tab === "Auditoría" && <Audit />}
      {tab === "Seguridad" && <RecaptchaSettings />}
    </div>
  );
}

function Franchises() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", city: "", pin: "" });
  const load = () => api.get("/franchises").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try {
      await api.post("/franchises", { name: form.name, city: form.city }, { headers: { "X-Master-Pin": form.pin } });
      toast.success("Franquicia creada"); setForm({ name: "", city: "", pin: "" }); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (f) => {
    await api.patch(`/franchises/${f.id}/status`); load();
  };
  const togglePerm = async (f) => {
    await api.patch(`/franchises/${f.id}/permissions`); load();
  };
  return (
    <div className="space-y-6">
      <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 flex flex-wrap gap-3 items-end">
        <Input data-testid="franchise-name-input" placeholder="Nombre (ej. Natur Sevilla)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-56" />
        <Input data-testid="franchise-city-input" placeholder="Ciudad" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-40" />
        <Input data-testid="franchise-pin-input" placeholder="PIN Maestro" type="password" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} className="w-36" />
        <Button data-testid="franchise-create-btn" onClick={create} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white"><Plus size={15} className="mr-1" /> Crear franquicia</Button>
      </div>
      <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm divide-y divide-neutral-100">
        {items.map((f) => (
          <div key={f.id} className="flex items-center justify-between p-4" data-testid="franchise-row">
            <div>
              <p className="font-medium text-sm">{f.name} <span className="text-neutral-400">· {f.city}</span></p>
              <p className="text-xs text-neutral-500">{f.students} alumnos · {f.teachers} profesores · Dakota {f.dakota_enabled ? "activa" : "apagada"}</p>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="franchise-perm-btn" onClick={() => togglePerm(f)}
                      title="Permiso para crear usuarios"
                      className={`text-xs font-semibold px-3 py-1.5 rounded-full ${f.can_create_users !== false ? "bg-blue-100 text-blue-700" : "bg-neutral-200 text-neutral-500"}`}>
                Alta usuarios: {f.can_create_users !== false ? "Sí" : "No"}
              </button>
              <button data-testid="franchise-toggle-btn" onClick={() => toggle(f)}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-full ${f.status === "active" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                {f.status === "active" ? "Activa" : "Inactiva"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersTab() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "ALUMNO", franchise_id: "", teacher_id: "", phone: "" });
  const [tempCred, setTempCred] = useState(null);
  const canCreate = ["SUPER_ADMIN", "FRANQUICIA"].includes(user.role);
  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    if (user.role === "SUPER_ADMIN") api.get("/franchises").then((r) => setFranchises(r.data));
  }, []);
  const create = async () => {
    try {
      const r = await api.post("/users", { ...form, password: form.password || null, franchise_id: form.franchise_id || null, teacher_id: form.teacher_id || null });
      setTempCred({ email: r.data.email, password: r.data.temp_password });
      toast.success("Usuario creado con credenciales iniciales"); load();
      setForm({ ...form, name: "", email: "", password: "" });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const teachers = users.filter((u) => u.role === "PROFESOR");
  const patchUser = async (id, body) => {
    try { await api.patch(`/users/${id}`, body); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/users/${id}/status`, { status }); toast.success(`Estado: ${status}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const resetPw = async (u) => {
    const pw = window.prompt(`Nueva contraseña temporal para ${u.email}:`);
    if (!pw) return;
    try { await api.post(`/users/${u.id}/password`, { new_password: pw }); toast.success("Contraseña restablecida (deberá cambiarla al entrar)"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delUser = async (u) => {
    const pin = window.prompt(`PIN Maestro para ELIMINAR a ${u.email}:`);
    if (!pin) return;
    try { await api.delete(`/users/${u.id}`, { headers: { "X-Master-Pin": pin } }); toast.success("Usuario eliminado"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6">
      {canCreate && (
        <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 grid md:grid-cols-3 gap-3">
          <Input data-testid="user-name-input" placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input data-testid="user-email-input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input data-testid="user-password-input" placeholder="Contraseña (vacío = temporal automática)" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <select data-testid="user-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="h-10 rounded-md border border-neutral-300 px-3 text-sm">
            {user.role === "SUPER_ADMIN" && <option value="FRANQUICIA">Franquicia</option>}
            <option value="PROFESOR">Profesor</option>
            <option value="ALUMNO">Alumno</option>
          </select>
          {user.role === "SUPER_ADMIN" && (
            <select data-testid="user-franchise-select" value={form.franchise_id} onChange={(e) => setForm({ ...form, franchise_id: e.target.value })} className="h-10 rounded-md border border-neutral-300 px-3 text-sm">
              <option value="">Franquicia…</option>
              {franchises.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          )}
          {form.role === "ALUMNO" && (
            <select data-testid="user-teacher-select" value={form.teacher_id} onChange={(e) => setForm({ ...form, teacher_id: e.target.value })} className="h-10 rounded-md border border-neutral-300 px-3 text-sm">
              <option value="">Profesor asignado…</option>
              {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          {form.role === "PROFESOR" && (
            <Input placeholder="WhatsApp (34600...)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          )}
          <Button data-testid="user-create-btn" onClick={create} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white">Crear usuario</Button>
        </div>
      )}
      {tempCred && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-5 text-sm" data-testid="temp-credentials-box">
          <p className="font-semibold text-amber-800 mb-1">Credenciales iniciales — entrégalas al usuario:</p>
          <p>Email: <strong>{tempCred.email}</strong> · Contraseña temporal: <strong data-testid="temp-password-value">{tempCred.password}</strong></p>
          <p className="text-xs text-amber-700 mt-1">En su primer acceso deberá establecer una contraseña nueva.</p>
        </div>
      )}
      <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm divide-y divide-neutral-100">
        {users.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid="user-row">
            <div className="min-w-0">
              <p className="font-medium text-sm">{u.name} <span className="text-neutral-400">· {u.email}</span></p>
              <p className="text-xs text-neutral-500">
                {ROLE_LABEL[u.role]}{u.role === "ALUMNO" ? ` · ${u.credits} créditos · ${u.points} puntos` : ""}
                {u.status && u.status !== "activo" && <span className="text-red-600 font-semibold"> · {u.status}</span>}
              </p>
            </div>
            {canCreate && u.role !== "SUPER_ADMIN" && (
              <div className="flex flex-wrap items-center gap-2">
                {u.role === "PROFESOR" && (
                  <>
                    <button data-testid="user-perm-blog-btn" onClick={() => patchUser(u.id, { can_blog: !(u.can_blog !== false) })}
                            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${u.can_blog !== false ? "bg-blue-100 text-blue-700" : "bg-neutral-200 text-neutral-500"}`}>
                      Blog: {u.can_blog !== false ? "Sí" : "No"}
                    </button>
                    <button data-testid="user-perm-mod-btn" onClick={() => patchUser(u.id, { can_moderate: !u.can_moderate })}
                            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${u.can_moderate ? "bg-blue-100 text-blue-700" : "bg-neutral-200 text-neutral-500"}`}>
                      Moderar: {u.can_moderate ? "Sí" : "No"}
                    </button>
                  </>
                )}
                <select data-testid="user-status-select" value={u.status || "activo"} onChange={(e) => setStatus(u.id, e.target.value)}
                        className="h-8 rounded-md border border-neutral-300 px-2 text-xs">
                  <option value="activo">Activo</option>
                  <option value="suspendido">Suspendido</option>
                  <option value="bloqueado">Bloqueado</option>
                  <option value="desactivado">Desactivado</option>
                </select>
                <button data-testid="user-resetpw-btn" onClick={() => resetPw(u)} title="Restablecer contraseña"
                        className="p-1.5 text-neutral-400 hover:text-orange-600"><KeyRound size={15} /></button>
                <button data-testid="user-delete-btn" onClick={() => delUser(u)} title="Eliminar"
                        className="p-1.5 text-neutral-400 hover:text-red-600"><Trash2 size={15} /></button>
              </div>
            )}
          </div>
        ))}
        {users.length === 0 && <p className="p-6 text-sm text-neutral-500">Sin usuarios.</p>}
      </div>
    </div>
  );
}

function KillSwitch() {
  const [settings, setSettings] = useState(null);
  const [franchises, setFranchises] = useState([]);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ mode: "global", franchise_id: "", reason: "", duration: "", pin: "" });
  const load = () => {
    api.get("/admin/settings").then((r) => setSettings(r.data));
    api.get("/franchises").then((r) => setFranchises(r.data));
    api.get("/admin/killswitch/events").then((r) => setEvents(r.data));
  };
  useEffect(() => { load(); }, []);
  const act = async (enabled) => {
    if (!form.reason) { toast.error("Indica el motivo (queda auditado)"); return; }
    try {
      await api.post("/admin/killswitch", { mode: form.mode, enabled, franchise_id: form.franchise_id || null, reason: form.reason, duration: form.duration },
                     { headers: { "X-Master-Pin": form.pin } });
      toast.success(enabled ? "Dakota activada" : "Dakota apagada");
      setForm({ ...form, reason: "", pin: "" }); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!settings) return <p className="text-neutral-500">Cargando…</p>;
  return (
    <div className="space-y-6">
      <div className={`rounded-xl p-6 border ${settings.dakota_global_enabled ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`} data-testid="killswitch-status">
        <p className="flex items-center gap-2 font-semibold"><Power size={18} /> Estado global de Dakota IA:
          <span className={settings.dakota_global_enabled ? "text-green-700" : "text-red-700"}>{settings.dakota_global_enabled ? "ACTIVA" : "APAGADA"}</span>
        </p>
      </div>
      <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 space-y-3">
        <h3 className="font-semibold">Botón de apagado de emergencia</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <select data-testid="killswitch-mode-select" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="h-10 rounded-md border border-neutral-300 px-3 text-sm">
            <option value="global">Apagado global</option>
            <option value="franchise">Apagado por franquicia</option>
          </select>
          {form.mode === "franchise" && (
            <select data-testid="killswitch-franchise-select" value={form.franchise_id} onChange={(e) => setForm({ ...form, franchise_id: e.target.value })} className="h-10 rounded-md border border-neutral-300 px-3 text-sm">
              <option value="">Selecciona franquicia…</option>
              {franchises.map((f) => <option key={f.id} value={f.id}>{f.name} (Dakota {f.dakota_enabled ? "activa" : "apagada"})</option>)}
            </select>
          )}
          <Input data-testid="killswitch-reason-input" placeholder="Motivo (obligatorio, queda auditado)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          <Input data-testid="killswitch-duration-input" placeholder="Duración estimada (ej. 2 horas)" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} />
          <Input data-testid="killswitch-pin-input" placeholder="PIN Maestro" type="password" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} />
        </div>
        <div className="flex gap-3">
          <Button data-testid="killswitch-off-btn" onClick={() => act(false)} className="rounded-full bg-red-600 hover:bg-red-700 text-white">Apagar IA</Button>
          <Button data-testid="killswitch-on-btn" onClick={() => act(true)} className="rounded-full bg-green-600 hover:bg-green-700 text-white">Activar IA</Button>
        </div>
      </div>
      <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm divide-y divide-neutral-100">
        <p className="p-4 text-sm font-semibold">Historial de apagados</p>
        {events.map((e) => (
          <div key={e.id} className="p-4 text-sm flex justify-between" data-testid="killswitch-event-row">
            <span>{e.enabled ? "🟢 Activación" : "🔴 Apagado"} {e.mode === "global" ? "global" : `de ${e.target}`} — {e.reason}{e.duration ? ` (${e.duration})` : ""}</span>
            <span className="text-neutral-400 text-xs">{e.user_email} · {fmtDate(e.date)}</span>
          </div>
        ))}
        {events.length === 0 && <p className="p-4 text-sm text-neutral-500">Sin eventos registrados.</p>}
      </div>
    </div>
  );
}

function Credits() {
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({ user_id: "", amount: 50, note: "" });
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role === "ALUMNO" || u.role === "PROFESOR")));
    api.get("/credits/plans").then((r) => setPlans(r.data));
  }, []);
  const assign = async () => {
    try {
      const r = await api.post("/credits/assign", { ...form, amount: Number(form.amount) });
      toast.success(`Créditos asignados. Saldo: ${r.data.credits}`);
      api.get("/users").then((rr) => setUsers(rr.data.filter((u) => u.role === "ALUMNO" || u.role === "PROFESOR")));
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-3 gap-4">
        {plans.map((p) => (
          <div key={p.name} className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6" data-testid={`plan-${p.name.toLowerCase()}`}>
            <p className="text-xs uppercase tracking-widest font-semibold text-orange-700">{p.name}</p>
            <p className="text-3xl font-semibold mt-2">{p.credits} <span className="text-sm font-normal text-neutral-500">créditos</span></p>
            <p className="text-sm text-neutral-500 mt-1">{p.price_eur} € / mes (referencia)</p>
          </div>
        ))}
      </div>
      <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 flex flex-wrap gap-3 items-end">
        <select data-testid="credits-user-select" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} className="h-10 rounded-md border border-neutral-300 px-3 text-sm w-64">
          <option value="">Selecciona usuario…</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.credits} créditos)</option>)}
        </select>
        <Input data-testid="credits-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-28" />
        <Input placeholder="Nota (ej. Plan Básico)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-52" />
        <Button data-testid="credits-assign-btn" onClick={assign} disabled={!form.user_id} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white">
          <Coins size={15} className="mr-1.5" /> Asignar créditos
        </Button>
      </div>
      <p className="text-xs text-neutral-500">Modelo prepago: precio crédito = 4 × coste real del proveedor IA (configurable). Pagos online se integrarán en una fase posterior.</p>
    </div>
  );
}

function Knowledge() {
  const [docs, setDocs] = useState([]);
  const [form, setForm] = useState({ title: "", doc_type: "Criterios de Calificación DGT", source: "", content: "", pin: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [dgtStatus, setDgtStatus] = useState(null);
  const load = () => api.get("/knowledge").then((r) => setDocs(r.data));
  const loadDgt = () => api.get("/dgt-codes/status").then((r) => setDgtStatus(r.data)).catch(() => {});
  useEffect(() => { load(); loadDgt(); }, []);

  const add = async () => {
    setBusy(true);
    try {
      const headers = { "X-Master-Pin": form.pin };
      let resp;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        resp = await api.post(`/knowledge/upload?title=${encodeURIComponent(form.title)}&doc_type=${encodeURIComponent(form.doc_type)}`, fd, { headers });
      } else {
        resp = await api.post("/knowledge", { title: form.title, doc_type: form.doc_type, source: form.source, content: form.content }, { headers });
      }
      const notice = resp.data?.extraction_notice;
      const st = resp.data?.extraction?.status;
      if (notice) {
        (st === "❌" ? toast.error : st === "⚠" ? toast.warning : toast.success)(notice);
      } else {
        toast.success("Documento subido (estado: pendiente). Apruébalo para que Dakota lo utilice.");
      }
      setForm({ ...form, title: "", source: "", content: "" }); setFile(null); load(); loadDgt();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const reindexAll = async () => {
    const pin = window.prompt("PIN Maestro para inyectar el conocimiento en Dakota:");
    if (!pin) return;
    setReindexing(true);
    try {
      const r = await api.post("/knowledge/reindex-all", null, { headers: { "X-Master-Pin": pin } });
      const d = r.data;
      const msg = d.empty_warning
        ? `Reindexado ${d.documents} docs (${d.chunks} fragmentos). ⚠️ No hay ningún "Criterios de Calificación DGT" vigente: el desplegable de Exámenes Prácticos quedará vacío.`
        : `Conocimiento inyectado: ${d.documents} docs · ${d.chunks} fragmentos · ${d.dgt_codes} claves DGT desde ${d.criteria_docs} documento(s) de criterios.`;
      toast.success(msg);
      load(); loadDgt();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setReindexing(false); }
  };

  const setStatus = async (d, status) => {
    const pin = window.prompt(`PIN Maestro para "${status}" el documento:`);
    if (!pin) return;
    try {
      await api.patch(`/knowledge/${d.id}/status?status=${status}`, null, { headers: { "X-Master-Pin": pin } });
      toast.success(`Documento ${status}`); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (d) => {
    const pin = window.prompt("PIN Maestro para eliminar:");
    if (!pin) return;
    try { await api.delete(`/knowledge/${d.id}`, { headers: { "X-Master-Pin": pin } }); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const [extractionView, setExtractionView] = useState(null); // {id, data}
  const viewExtraction = async (d) => {
    if (extractionView?.id === d.id) { setExtractionView(null); return; }
    try {
      const r = await api.get(`/knowledge/${d.id}/extraction`);
      setExtractionView({ id: d.id, data: r.data });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const forceReindex = async (d) => {
    const includeDoubtful = window.confirm(
      `Forzar reindexado de "${d.title}".\n\n¿Incluir también las claves DUDOSAS en el catálogo?\n\nAceptar = incluir dudosas · Cancelar = solo claves limpias`
    );
    const pin = window.prompt("PIN Maestro para forzar reindexado:");
    if (!pin) return;
    try {
      const r = await api.post(`/knowledge/${d.id}/force-reindex?include_doubtful=${includeDoubtful}`, null,
                                { headers: { "X-Master-Pin": pin } });
      toast.success(`Forzado: ${r.data.dgt_codes} claves en el catálogo desde ${r.data.criteria_docs} doc(s).`);
      load(); loadDgt(); setExtractionView(null);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const STATUS_STYLE = { aprobado: "bg-green-100 text-green-700", pendiente: "bg-amber-100 text-amber-700",
                         borrador: "bg-neutral-200 text-neutral-600", archivado: "bg-neutral-200 text-neutral-500", activo: "bg-amber-100 text-amber-700" };

  const [metaEdit, setMetaEdit] = useState(null);
  const [metaForm, setMetaForm] = useState({});
  const openMeta = (d) => {
    setMetaEdit(d.id);
    setMetaForm({ year: d.year || "", edition: d.edition || "", version: d.version || "",
                  effective_date: d.effective_date || "", vigencia_manual: d.vigencia_manual || "auto" });
  };
  const saveMeta = async (d) => {
    const pin = window.prompt("PIN Maestro para guardar los metadatos:");
    if (!pin) return;
    try {
      await api.patch(`/knowledge/${d.id}/meta`, {
        year: metaForm.year ? Number(metaForm.year) : null, edition: metaForm.edition || null,
        version: metaForm.version || null, effective_date: metaForm.effective_date || null,
        vigencia_manual: metaForm.vigencia_manual,
      }, { headers: { "X-Master-Pin": pin } });
      toast.success("Metadatos actualizados; vigencia recalculada"); setMetaEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const metaLine = (d) => {
    const parts = [];
    if (d.year) parts.push(`Año ${d.year}`);
    if (d.edition) parts.push(`Edición ${d.edition}`);
    if (d.version) parts.push(`Versión ${d.version}`);
    if (d.effective_date) parts.push(`Vigor: ${d.effective_date}`);
    if (d.pub_date) parts.push(`Publicación: ${d.pub_date}`);
    return parts.join(" · ");
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-semibold flex items-center gap-2"><BookMarked size={17} className="text-orange-600" /> Alimentar a Dakota (RAG documental)</h3>
          <Button data-testid="knowledge-reindex-all-btn" onClick={reindexAll} disabled={reindexing}
                  className="rounded-full bg-neutral-900 hover:bg-neutral-800 text-white h-9 text-xs">
            {reindexing ? "Inyectando…" : "🧠 Inyectar conocimiento"}
          </Button>
        </div>
        <p className="text-xs text-neutral-500">Sube un PDF/TXT o pega el texto. Los documentos entran en estado <strong>pendiente</strong>; solo los <strong>aprobados</strong> alimentan las respuestas de Dakota, que citará la fuente y el nivel de confianza.</p>
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Regla de normativas: si hay varias versiones de una misma normativa (o el nombre contiene <strong>OBSOLETO</strong>), Dakota usa la <strong>vigente más reciente</strong> como criterio operativo y reserva las anteriores solo para consultas históricas y comparativas.</p>
        <p className="text-xs text-neutral-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
          <strong>Módulo Exámenes Prácticos:</strong> el desplegable de claves DGT se construye <u>exclusivamente</u> desde los documentos de tipo
          <span className="font-semibold"> "Criterios de Calificación DGT"</span> aprobados y vigentes. El resto del repositorio (contratos, seguros, pedagogía…) puede enriquecer las sugerencias de Dakota pero <u>no</u> aporta claves al selector.
        </p>
        {dgtStatus && (
          <div data-testid="dgt-status-panel" className={`text-xs rounded-lg px-3 py-2 border ${dgtStatus.empty_warning ? "bg-red-50 border-red-200 text-red-700" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}>
            {dgtStatus.empty_warning ? (
              <span>⚠️ No hay ningún <strong>Criterios de Calificación DGT</strong> aprobado y vigente. El desplegable del módulo Exámenes Prácticos está vacío hasta que subas el PDF oficial y lo apruebes.</span>
            ) : (
              <span>Catálogo DGT activo: <strong>{dgtStatus.codes}</strong> claves desde <strong>{dgtStatus.criteria_docs.length}</strong> documento(s) vigente(s) ({dgtStatus.criteria_docs.map(d => d.title).join(", ")}).</span>
            )}
          </div>
        )}
        <div className="grid md:grid-cols-3 gap-3">
          <Input data-testid="knowledge-title-input" placeholder="Título del documento" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <select data-testid="knowledge-type-select" value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} className="h-10 rounded-md border border-neutral-300 px-3 text-sm">
            {["Criterios de Calificación DGT", "PDF", "Documentación DGT", "BOE", "Manual", "Normativa", "Documentación interna", "Material docente"].map((t) => <option key={t}>{t}</option>)}
          </select>
          <Input placeholder="Fuente / referencia" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
        </div>
        <Textarea data-testid="knowledge-content-input" rows={5} placeholder="Pega aquí el texto del documento (normativa, manual, criterios de examen…)" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
        <div className="flex flex-wrap items-center gap-3">
          <input type="file" accept=".pdf,.txt" data-testid="knowledge-file-input" onChange={(e) => setFile(e.target.files[0] || null)} className="text-xs" title="PDF o TXT, hasta 50 MB" />
          <Input data-testid="knowledge-pin-input" placeholder="PIN Maestro" type="password" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} className="w-36" />
          <Button data-testid="knowledge-add-btn" onClick={add} disabled={busy || (!form.title && !file) || (!form.content && !file)}
                  className="rounded-full bg-orange-600 hover:bg-orange-700 text-white">
            {busy ? "Procesando…" : "Subir documento"}
          </Button>
        </div>
      </div>
      <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm divide-y divide-neutral-100">
        <p className="p-4 text-sm font-semibold">Qué está leyendo Dakota (solo los aprobados participan en respuestas)</p>
        {docs.map((d) => (
          <div key={d.id} className="p-4" data-testid="knowledge-row">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  {d.title} <span className="text-neutral-400 font-normal">· {d.doc_type}</span>
                  {d.doc_type === "Criterios de Calificación DGT" && (
                    <span data-testid="knowledge-criteria-badge" className="ml-2 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-orange-600 text-white align-middle">Fuente exámenes</span>
                  )}
                  {d.extraction?.status && (
                    <span data-testid="knowledge-extraction-badge" title={d.extraction.message}
                          className={`ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full align-middle ${d.extraction.status === "✅" ? "bg-emerald-100 text-emerald-700" : d.extraction.status === "⚠" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                      {d.extraction.status} {d.extraction.codes} claves
                    </span>
                  )}
                  {d.manual_force && (
                    <span className="ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 align-middle" title="Reindexado manual forzado por el administrador">Forzado</span>
                  )}
                </p>
                <p className="text-xs text-neutral-400">{d.uploaded_by} · {fmtDate(d.date)} · {d.chunks || 0} fragmentos indexados</p>
                {metaLine(d) && <p className="text-[11px] text-neutral-500 mt-0.5" data-testid="knowledge-meta-line">{metaLine(d)}</p>}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span data-testid="knowledge-vigencia-badge" className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${d.vigencia === "historico" ? "bg-neutral-200 text-neutral-600" : "bg-emerald-100 text-emerald-700"}`}>
                  {d.vigencia === "historico" ? "Histórico" : "Vigente"}{d.vigencia_manual ? " (manual)" : ""}
                </span>
                <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLE[d.status] || "bg-neutral-200"}`}>{d.status}</span>
                {d.status !== "aprobado" && <Button size="sm" data-testid="knowledge-approve-btn" onClick={() => setStatus(d, "aprobado")} className="rounded-full h-7 text-xs bg-green-600 hover:bg-green-700 text-white">Aprobar</Button>}
                {d.status === "aprobado" && <Button size="sm" variant="outline" data-testid="knowledge-archive-btn" onClick={() => setStatus(d, "archivado")} className="rounded-full h-7 text-xs">Archivar</Button>}
                <Button size="sm" variant="outline" data-testid="knowledge-meta-btn" onClick={() => (metaEdit === d.id ? setMetaEdit(null) : openMeta(d))} className="rounded-full h-7 text-xs">Metadatos</Button>
                {d.doc_type === "Criterios de Calificación DGT" && (
                  <>
                    <Button size="sm" variant="outline" data-testid="knowledge-view-extraction-btn"
                            onClick={() => viewExtraction(d)}
                            className="rounded-full h-7 text-xs">Ver extracción</Button>
                    <Button size="sm" data-testid="knowledge-force-reindex-btn"
                            onClick={() => forceReindex(d)}
                            className="rounded-full h-7 text-xs bg-purple-600 hover:bg-purple-700 text-white">Forzar</Button>
                  </>
                )}
                <button data-testid="knowledge-delete-btn" onClick={() => del(d)} className="p-1.5 text-neutral-400 hover:text-red-600"><Trash2 size={14} /></button>
              </div>
            </div>
            {extractionView?.id === d.id && extractionView.data && (
              <div className="mt-3 bg-neutral-50 rounded-xl p-4 text-xs space-y-3" data-testid="knowledge-extraction-panel">
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`font-semibold px-2 py-0.5 rounded-full ${extractionView.data.extraction_status === "✅" ? "bg-emerald-100 text-emerald-700" : extractionView.data.extraction_status === "⚠" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                    Estado {extractionView.data.extraction_status}
                  </span>
                  <span>Claves: <strong>{extractionView.data.counts.codes}</strong></span>
                  <span>Dudosas: <strong>{extractionView.data.counts.doubtful}</strong></span>
                  <span>Huérfanas: <strong>{extractionView.data.counts.orphans}</strong></span>
                  <span>{extractionView.data.contributes_to_catalog ? "Contribuye al catálogo" : "No contribuye (pendiente/histórico/otro tipo)"}</span>
                </div>
                {extractionView.data.headers_found?.length > 0 && (
                  <div><span className="text-neutral-500">Cabeceras oficiales detectadas:</span> {extractionView.data.headers_found.join(" · ")}</div>
                )}
                <div>
                  <div className="text-neutral-500 mb-1">Claves detectadas ({extractionView.data.codes.length}):</div>
                  <div className="max-h-40 overflow-auto grid grid-cols-2 gap-x-4 gap-y-0.5">
                    {extractionView.data.codes.map((c) => (
                      <div key={c.code} className="truncate"><strong>{c.code}</strong> · {c.description}</div>
                    ))}
                  </div>
                </div>
                {extractionView.data.doubtful.length > 0 && (
                  <div>
                    <div className="text-amber-700 mb-1">Dudosas ({extractionView.data.doubtful.length}) — coinciden con el filtro anti-ruido:</div>
                    <div className="max-h-32 overflow-auto grid grid-cols-2 gap-x-4 gap-y-0.5">
                      {extractionView.data.doubtful.map((c) => (
                        <div key={c.code} className="truncate"><strong>{c.code}</strong> · {c.description}</div>
                      ))}
                    </div>
                  </div>
                )}
                {extractionView.data.orphans.length > 0 && (
                  <div>
                    <span className="text-neutral-500">Huérfanas (mencionadas sin descripción parseable):</span> {extractionView.data.orphans.join(", ")}
                  </div>
                )}
                <p className="text-neutral-500">Si el PDF DGT tiene una maquetación en columnas/tablas y la extracción es imperfecta, usa <strong>Forzar</strong> para que el documento contribuya al catálogo tras tu revisión manual.</p>
              </div>
            )}
            {metaEdit === d.id && (
              <div className="mt-3 bg-neutral-50 rounded-xl p-4 grid md:grid-cols-5 gap-2 items-end" data-testid="knowledge-meta-editor">
                <div><label className="text-[11px] text-neutral-500 block mb-1">Año</label>
                  <Input value={metaForm.year} onChange={(e) => setMetaForm({ ...metaForm, year: e.target.value })} placeholder="2026" className="h-8 text-xs" data-testid="meta-year-input" /></div>
                <div><label className="text-[11px] text-neutral-500 block mb-1">Edición</label>
                  <Input value={metaForm.edition} onChange={(e) => setMetaForm({ ...metaForm, edition: e.target.value })} className="h-8 text-xs" data-testid="meta-edition-input" /></div>
                <div><label className="text-[11px] text-neutral-500 block mb-1">Versión</label>
                  <Input value={metaForm.version} onChange={(e) => setMetaForm({ ...metaForm, version: e.target.value })} className="h-8 text-xs" data-testid="meta-version-input" /></div>
                <div><label className="text-[11px] text-neutral-500 block mb-1">Entrada en vigor</label>
                  <Input type="date" value={metaForm.effective_date} onChange={(e) => setMetaForm({ ...metaForm, effective_date: e.target.value })} className="h-8 text-xs" data-testid="meta-effective-input" /></div>
                <div><label className="text-[11px] text-neutral-500 block mb-1">Vigencia</label>
                  <select value={metaForm.vigencia_manual} onChange={(e) => setMetaForm({ ...metaForm, vigencia_manual: e.target.value })}
                          className="h-8 w-full rounded-md border border-neutral-300 px-2 text-xs" data-testid="meta-vigencia-select">
                    <option value="auto">Automática</option>
                    <option value="vigente">Forzar vigente</option>
                    <option value="historico">Forzar histórico</option>
                  </select></div>
                <Button size="sm" data-testid="meta-save-btn" onClick={() => saveMeta(d)} className="rounded-full h-8 text-xs bg-neutral-900 text-white md:col-span-5 w-fit">Guardar metadatos (PIN)</Button>
              </div>
            )}
          </div>
        ))}
        {docs.length === 0 && <p className="p-4 text-sm text-neutral-500">Sin documentos cargados.</p>}
      </div>
    </div>
  );
}

function LovManager() {
  const [lists, setLists] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const active = lists.find((l) => l.key === activeKey);

  const load = async () => {
    const r = await api.get("/lov");
    setLists(r.data);
    if (!activeKey && r.data.length) setActiveKey(r.data[0].key);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const pin = () => window.prompt("PIN Maestro:");

  const addValue = async () => {
    if (!newLabel.trim() || !active) return;
    const p = pin(); if (!p) return;
    setBusy(true);
    try {
      await api.post(`/lov/${active.key}`, { label: newLabel.trim() }, { headers: { "X-Master-Pin": p } });
      setNewLabel(""); await load(); toast.success("Valor añadido");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const editValue = async (v) => {
    const label = window.prompt("Editar valor:", v.label); if (!label) return;
    const p = pin(); if (!p) return;
    try { await api.patch(`/lov/${active.key}/${v.id}`, { label }, { headers: { "X-Master-Pin": p } }); await load(); toast.success("Actualizado"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delValue = async (v) => {
    if (!window.confirm(`¿Eliminar "${v.label}"?`)) return;
    const p = pin(); if (!p) return;
    try { await api.delete(`/lov/${active.key}/${v.id}`, { headers: { "X-Master-Pin": p } }); await load(); toast.success("Eliminado"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const dupValue = async (v) => {
    const p = pin(); if (!p) return;
    try { await api.post(`/lov/${active.key}/${v.id}/duplicate`, null, { headers: { "X-Master-Pin": p } }); await load(); toast.success("Duplicado"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const move = async (v, delta) => {
    const idx = active.values.findIndex((x) => x.id === v.id);
    const j = idx + delta;
    if (j < 0 || j >= active.values.length) return;
    const ids = active.values.map((x) => x.id);
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    const p = pin(); if (!p) return;
    try { await api.post(`/lov/${active.key}/reorder`, { ids }, { headers: { "X-Master-Pin": p } }); await load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid md:grid-cols-[220px_1fr] gap-6" data-testid="lov-manager">
      <aside className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-2">
        <p className="text-[11px] font-semibold uppercase text-neutral-400 px-2 pt-2 pb-1">Catálogos</p>
        <div className="space-y-0.5">
          {lists.map((l) => (
            <button key={l.key} data-testid={`lov-tab-${l.key}`} onClick={() => setActiveKey(l.key)}
                    className={`w-full text-left text-sm px-3 py-2 rounded-lg transition-colors ${activeKey === l.key ? "bg-neutral-900 text-white" : "hover:bg-neutral-100 text-neutral-700"}`}>
              {l.label}
              <span className={`ml-2 text-[10px] ${activeKey === l.key ? "opacity-70" : "text-neutral-400"}`}>{l.values.length}</span>
            </button>
          ))}
          <button data-testid="lov-tab-PROFESORES_ASIGNABLES" onClick={() => setActiveKey("PROFESORES_ASIGNABLES")}
                  className={`w-full text-left text-sm px-3 py-2 rounded-lg transition-colors ${activeKey === "PROFESORES_ASIGNABLES" ? "bg-neutral-900 text-white" : "hover:bg-neutral-100 text-neutral-700"}`}>
            Profesores asignables <span className="ml-2 text-[10px] opacity-60">(dinámico)</span>
          </button>
        </div>
      </aside>
      <section className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 space-y-4">
        {activeKey === "PROFESORES_ASIGNABLES" ? (
          <DynamicProfList />
        ) : active ? (
          <>
            <div>
              <h3 className="font-semibold" data-testid="lov-active-label">{active.label}</h3>
              <p className="text-xs text-neutral-500">Clave técnica: <code className="text-neutral-600">{active.key}</code>. Estos valores se usan en desplegables de toda la app.</p>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <Input data-testid="lov-new-input" placeholder="Nuevo valor…" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} className="max-w-sm" />
              <Button data-testid="lov-add-btn" onClick={addValue} disabled={busy || !newLabel.trim()} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white h-9 text-xs">➕ Añadir</Button>
            </div>
            <div className="divide-y divide-neutral-100 border border-neutral-200/70 rounded-xl overflow-hidden">
              {active.values.map((v, i) => (
                <div key={v.id} className="p-3 flex items-center gap-2 hover:bg-neutral-50" data-testid="lov-value-row">
                  <span className="text-[11px] text-neutral-400 w-6">{i + 1}.</span>
                  <span className="flex-1 text-sm">{v.label}</span>
                  <button title="Subir" onClick={() => move(v, -1)} className="text-neutral-400 hover:text-neutral-900 text-sm px-1">↑</button>
                  <button title="Bajar" onClick={() => move(v, 1)} className="text-neutral-400 hover:text-neutral-900 text-sm px-1">↓</button>
                  <button data-testid="lov-edit-btn" title="Editar" onClick={() => editValue(v)} className="text-neutral-400 hover:text-orange-600 text-sm px-1.5">✏</button>
                  <button data-testid="lov-duplicate-btn" title="Duplicar" onClick={() => dupValue(v)} className="text-neutral-400 hover:text-blue-600 text-sm px-1.5">📋</button>
                  <button data-testid="lov-delete-btn" title="Eliminar" onClick={() => delValue(v)} className="text-neutral-400 hover:text-red-600 text-sm px-1.5">🗑</button>
                </div>
              ))}
              {active.values.length === 0 && <p className="p-4 text-sm text-neutral-500">Lista vacía. Añade el primer valor.</p>}
            </div>
          </>
        ) : (
          <p className="text-sm text-neutral-500">Selecciona un catálogo.</p>
        )}
      </section>
    </div>
  );
}

function DynamicProfList() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/lov/PROFESORES_ASIGNABLES").then((r) => setD(r.data)); }, []);
  if (!d) return <p className="text-sm text-neutral-500">Cargando…</p>;
  return (
    <div>
      <h3 className="font-semibold">Profesores asignables</h3>
      <p className="text-xs text-neutral-500 mb-3">Lista <strong>dinámica</strong>: se genera desde <em>Usuarios → Rol Profesor activo</em>. Para añadir o modificar, ve a la pestaña <strong>Usuarios</strong>.</p>
      <div className="divide-y divide-neutral-100 border border-neutral-200/70 rounded-xl">
        {d.values.map((v, i) => (
          <div key={v.id} className="p-3 text-sm flex items-center gap-2"><span className="text-[11px] text-neutral-400 w-6">{i + 1}.</span>{v.label}</div>
        ))}
        {d.values.length === 0 && <p className="p-4 text-sm text-neutral-500">Sin profesores activos.</p>}
      </div>
    </div>
  );
}

function ArticleGen() {
  const nav = useNavigate();
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/generate-article", { topic });
      setResult(r.data);
      toast.success("Artículo generado por Dakota");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const toBlog = () => {
    sessionStorage.setItem("natur_article_draft", JSON.stringify(result));
    nav("/app/blog");
  };
  return (
    <div className="space-y-6 max-w-3xl">
      <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 dakota-glow">
        <h3 className="font-semibold flex items-center gap-2 mb-2"><Sparkles size={17} className="text-orange-600" /> Generación de artículos con Dakota</h3>
        <p className="text-sm text-neutral-500 mb-4">Solo matriz. Dakota genera SEO Title, Meta Description, H1, estructura y contenido. Dakota asesora, no publica automáticamente.</p>
        <div className="flex gap-2">
          <Input data-testid="article-topic-input" placeholder="Tema, ej: Distancias de seguridad en autopista" value={topic} onChange={(e) => setTopic(e.target.value)} />
          <Button data-testid="article-generate-btn" onClick={generate} disabled={busy || !topic} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white shrink-0">
            {busy ? "Generando…" : "Generar"}
          </Button>
        </div>
      </div>
      {result && (
        <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 space-y-3" data-testid="article-result">
          <p className="text-sm"><strong>SEO Title:</strong> {result.seo_title}</p>
          <p className="text-sm"><strong>Meta Description:</strong> {result.meta_description}</p>
          <p className="text-sm"><strong>H1:</strong> {result.h1}</p>
          <p className="text-sm"><strong>URL:</strong> {result.url_slug}</p>
          <div className="text-sm whitespace-pre-wrap bg-neutral-50 rounded-lg p-4 max-h-72 overflow-y-auto">{result.content}</div>
          <Button data-testid="article-to-blog-btn" onClick={toBlog} className="rounded-full bg-neutral-900 text-white">Llevar al formulario de blog</Button>
        </div>
      )}
    </div>
  );
}

function WooTab() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ store_url: "", consumer_key: "", consumer_secret: "", pin: "" });
  const [resource, setResource] = useState("products");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadStatus = () => api.get("/woo/status").then((r) => setStatus(r.data));
  useEffect(() => { loadStatus(); }, []);
  const save = async () => {
    try {
      const { pin, ...body } = form;
      await api.put("/admin/woocommerce", body, { headers: { "X-Master-Pin": pin } });
      toast.success("Configuración guardada, comprobando conexión…");
      loadStatus();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const fetchRes = async (res) => {
    setResource(res); setBusy(true); setData(null);
    try {
      const r = await api.get(`/woo/${res}`);
      setData(r.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-6">
      <div className={`rounded-2xl p-5 border ${status?.connected ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`} data-testid="woo-status">
        <p className="font-semibold flex items-center gap-2"><ShoppingCart size={17} />
          {status?.connected ? `Conectado a ${status.store_url}` : status?.store_url ? `Sin conexión con ${status.store_url}${status.error ? ` — ${status.error}` : ""}` : "WooCommerce no conectado"}
        </p>
        {!status?.connected && <p className="text-xs text-neutral-500 mt-1">Genera las claves en tu tienda: WooCommerce → Ajustes → Avanzado → API REST → Añadir clave (permiso Lectura/Escritura).</p>}
      </div>
      <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 grid md:grid-cols-2 gap-3">
        <Input data-testid="woo-url-input" placeholder="URL de la tienda (https://naturautoescuela.es)" value={form.store_url} onChange={(e) => setForm({ ...form, store_url: e.target.value })} />
        <Input data-testid="woo-key-input" placeholder="Consumer Key (ck_...)" value={form.consumer_key} onChange={(e) => setForm({ ...form, consumer_key: e.target.value })} />
        <Input data-testid="woo-secret-input" placeholder="Consumer Secret (cs_...)" type="password" value={form.consumer_secret} onChange={(e) => setForm({ ...form, consumer_secret: e.target.value })} />
        <div className="flex gap-2">
          <Input data-testid="woo-pin-input" placeholder="PIN Maestro" type="password" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} />
          <Button data-testid="woo-save-btn" onClick={save} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white shrink-0">Conectar</Button>
        </div>
      </div>
      {status?.connected && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {[["products", "Productos"], ["orders", "Pedidos"], ["customers", "Clientes"], ["coupons", "Cupones"], ["reports", "Informes"], ["subscriptions", "Suscripciones"]].map(([k, l]) => (
              <button key={k} data-testid={`woo-res-${k}`} onClick={() => fetchRes(k)}
                      className={`text-sm px-4 py-1.5 rounded-full ${resource === k && data ? "bg-neutral-900 text-white" : "bg-white border border-neutral-200"}`}>{l}</button>
            ))}
          </div>
          {busy && <p className="text-sm text-neutral-500">Consultando tienda…</p>}
          {data && (
            <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-4 text-xs" data-testid="woo-data">
              <p className="text-neutral-500 mb-2">{data.total ? `${data.total} elementos` : ""}</p>
              <pre className="overflow-auto max-h-96 whitespace-pre-wrap">{JSON.stringify(data.data, null, 1).slice(0, 8000)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AnalyticsTab() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)); }, []);
  if (!a) return <p className="text-neutral-500">Cargando…</p>;
  const max = Math.max(...a.visits_by_day.map((v) => v.count), 1);
  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2"><BarChart3 size={17} className="text-orange-600" /> Visitas (últimos 30 días)</h3>
        {a.visits_by_day.length === 0 ? <p className="text-sm text-neutral-500">Sin datos todavía.</p> : (
          <div className="flex items-end gap-1 h-32">
            {a.visits_by_day.map((v) => (
              <div key={v.date} title={`${v.date}: ${v.count}`} className="flex-1 bg-orange-500/80 rounded-t"
                   style={{ height: `${Math.max((v.count / max) * 100, 4)}%` }} />
            ))}
          </div>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6">
          <h3 className="font-semibold mb-3">Módulos más utilizados</h3>
          <ul className="space-y-1.5 text-sm">
            {a.top_modules.map((m) => (
              <li key={m.module} className="flex justify-between border-b border-neutral-100 pb-1">
                <span className="capitalize">{m.module}</span><span className="font-semibold text-orange-700">{m.count}</span>
              </li>
            ))}
            {a.top_modules.length === 0 && <li className="text-neutral-500">Sin datos.</li>}
          </ul>
        </div>
        <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6" data-testid="online-users">
          <h3 className="font-semibold mb-3">Usuarios conectados (15 min)</h3>
          <ul className="space-y-1.5 text-sm">
            {a.online_users.map((u, i) => (
              <li key={i} className="flex justify-between border-b border-neutral-100 pb-1">
                <span>{u.name}</span><span className="text-neutral-400 text-xs">{u.role}</span>
              </li>
            ))}
            {a.online_users.length === 0 && <li className="text-neutral-500">Nadie conectado ahora.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}

function CommentsTab() {
  const [comments, setComments] = useState([]);
  const load = () => api.get("/admin/comments").then((r) => setComments(r.data));
  useEffect(() => { load(); }, []);
  const moderate = async (id, status) => {
    try { await api.patch(`/comments/${id}?status=${status}`); toast.success(`Comentario ${status}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm divide-y divide-neutral-100" data-testid="comments-tab">
      <p className="p-4 text-sm font-semibold">Comentarios pendientes de aprobación</p>
      {comments.map((c) => (
        <div key={c.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid="pending-comment-row">
          <div className="min-w-0">
            <p className="text-sm">{c.content}</p>
            <p className="text-xs text-neutral-400">{c.user_name} · {fmtDate(c.date)}</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="comment-approve-btn" size="sm" onClick={() => moderate(c.id, "aprobado")} className="rounded-full bg-green-600 hover:bg-green-700 text-white h-8">Aprobar</Button>
            <Button data-testid="comment-reject-btn" size="sm" variant="outline" onClick={() => moderate(c.id, "rechazado")} className="rounded-full h-8 border-red-300 text-red-600">Rechazar</Button>
          </div>
        </div>
      ))}
      {comments.length === 0 && <p className="p-4 text-sm text-neutral-500">No hay comentarios pendientes.</p>}
    </div>
  );
}

function Audit() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setLogs(r.data)); }, []);
  return (
    <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm divide-y divide-neutral-100" data-testid="audit-table">
      <p className="p-4 text-sm font-semibold flex items-center gap-2"><ScrollText size={16} /> Logs de auditoría (usuario, fecha, IP, acción, franquicia)</p>
      <div className="max-h-[60vh] overflow-y-auto divide-y divide-neutral-100">
        {logs.map((l, i) => (
          <div key={i} className="p-3 text-xs grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="audit-row">
            <span className="font-medium truncate">{l.user_email}</span>
            <span className="text-orange-700 font-semibold">{l.action}</span>
            <span className="truncate text-neutral-500">{l.detail}</span>
            <span className="text-neutral-400">{l.ip}</span>
            <span className="text-neutral-400">{new Date(l.date).toLocaleString("es-ES")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
